// Übernahme des Prototyp-Stands (localStorage „sentenzo.proto.v2“ bzw. „sentenzo.proto.v2.<deck>“ oder die Exportdatei „sentenzo-fortschritt.json“).
// Der Prototyp speicherte den fertigen Planer-Zustand je Einheit (units) und die letzten 500 Bewertungen (log). Die App braucht Ereignisse.
//  - Jede Bewertung im log wird ein Ereignis mit fester ID (gleiche Datei zweimal übernommen = keine Dubletten).
//  - Schnell-Check „kenne ich“ und Video-Sammlung standen nicht im log: dafür entsteht je Einheit ein Ereignis „Gut“ zum Einführungszeitpunkt.
//  - Fehlen Bewertungen (log auf 500 gekürzt), wird das gemeldet; der Stand dieser Einheiten weicht dann vom Prototyp ab.
//  - Zeitreise: Bewertungen in der Zukunft werden gemeinsam so weit zurückgeschoben, dass die jüngste jetzt liegt (Abstände bleiben gleich).
//    Sonst lehnt der Server sie ab (review_events: reviewed_at höchstens 1 Tag in der Zukunft) und die Warteschlange stockt.
import { stableUuid } from './ids.js';

export const PROTO_PREFIX = 'sentenzo.proto.v2';

// localStorage-Schlüssel des Prototyps -> Deck-ID der App
export function protoKeyToDeck(key) {
  // Unprefixed keys belong to the original Italian prototype, regardless of the open course.
  if (key === PROTO_PREFIX) return 'it-demo';
  if (!key.startsWith(PROTO_PREFIX + '.')) return null;
  const d = key.slice(PROTO_PREFIX.length + 1);
  return /^[a-z]{2,3}-/.test(d) ? d : `it-${d}`;
}

// mainOf(uid) liefert den Hauptsatz einer Einheit (für Ereignisse aus Schnell-Check und Sammlung ohne gespeicherten Satz).
export function convertPrototype(P, deckId, nowMs = Date.now(), mainOf = () => '') {
  if (!P || typeof P !== 'object' || P.v !== 2 || typeof P.units !== 'object') throw new Error('Kein Prototyp-Stand (Version 2) erkannt.');
  const events = [], quiet = [], seenKeys = {};
  const mk = (t, u, s, m, g, kind) => {
    const base = `proto|${deckId}|${kind}|${t}|${u}|${m}|${g}|${s}`;
    const n = (seenKeys[base] = (seenKeys[base] || 0) + 1);
    const e = { id: stableUuid(`${base}|${n}`), deck: deckId, unit_id: u, direction: m === 'p' ? 'p' : 'r', sentence_id: s, rating: g, reviewed_at: t };
    events.push(e);
    return e;
  };
  for (const x of P.log || []) if (x && x.u && x.s && x.t && x.g >= 1 && x.g <= 4) mk(x.t, x.u, x.s, x.m, x.g, 'log');

  const count = {};
  for (const e of events) count[e.unit_id + '|' + e.direction] = (count[e.unit_id + '|' + e.direction] || 0) + 1;
  const flags = {}, intro = {}, incomplete = [];
  let synthesized = 0;
  for (const [uid, u] of Object.entries(P.units)) {
    if (!u || typeof u !== 'object') continue;
    if (u.known) flags[uid] = { known: true };
    if (u.fromCollection) flags[uid] = { fromCollection: u.fromCollection };
    if (!u.last) { if (u.introAt) intro[uid] = u.introAt; continue; }          // gezeigt, aber noch nicht bewertet
    const nr = count[uid + '|r'] || 0;
    if (!nr && (u.known || u.fromCollection)) {
      const e = mk(u.introAt || u.last, uid, u.lastSid || mainOf(uid) || '', 'r', 3, 'start'); quiet.push(e.id); synthesized++;
    } else if (u.f && nr < u.f.reps) incomplete.push(uid);
    if (u.p && u.p.f && (count[uid + '|p'] || 0) < u.p.f.reps) incomplete.push(uid + ' (Sprechen)');
  }
  // Ereignisse ohne Satz-ID nimmt der Server nicht an (sentence_id not null); sie werden gezählt und gemeldet, nicht übernommen.
  const usable = events.filter(e => e.sentence_id);
  const dropped = events.length - usable.length;

  const maxT = usable.reduce((m, e) => Math.max(m, e.reviewed_at), -Infinity);
  const shiftMs = maxT > nowMs ? maxT - nowMs : 0;
  if (shiftMs) {
    for (const e of usable) e.reviewed_at -= shiftMs;
    for (const k of Object.keys(intro)) intro[k] -= shiftMs;
  }
  const hidden = { s: Object.assign({}, P.hidden && P.hidden.s), u: Object.assign({}, P.hidden && P.hidden.u) };
  const reports = (P.reports || []).filter(r => r && r.sid).map(r => ({ id: stableUuid(`proto-report|${deckId}|${r.sid}|${r.t}`), sid: r.sid, reasons: r.reasons || [], text: r.text || '', t: r.t || nowMs }));
  return {
    events: usable, quiet, intro, flags, hidden, reports,
    settings: P.settings ? { newPerDay: P.settings.newPerDay, retention: P.settings.retention, rate: P.settings.rate } : null,
    profile: P.profile || null, collection: P.collection || {},
    report: { events: usable.length, synthesized, incomplete, dropped, shiftDays: Math.round(shiftMs / 864e5 * 10) / 10, units: Object.keys(P.units).length },
  };
}
